import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
  Modal,
  Pressable,
  StyleSheet,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../../../Admin/constants/adminColors';
import SectionCard from '../SectionCard';
import {
  CoachingHistoryItem,
  CoachingItemCategory,
  FollowUpResult,
} from '../../types/beginActivity.common.types';
import { CoachingHistoryProps } from './CoachingHistory.types';
import styles from './CoachingHistory.styles';

// ─── Constants ────────────────────────────────────────────────────────────────
// '' lets a user revert a selection back to not-yet-reviewed.
const RESULT_OPTIONS: FollowUpResult[] = [
  '',
  'Shows Improvement',
  'No Improvement Shown',
  'Complete',
];
const MIN_FEEDBACK_CHARS = 10;

function resultOptionLabel(option: FollowUpResult): string {
  return option || 'Pending, select a result';
}

function requiresFeedback(result: FollowUpResult): boolean {
  return result === 'Shows Improvement' || result === 'No Improvement Shown';
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Category badge ───────────────────────────────────────────────────────────
function CategoryBadge({ category }: { category: CoachingItemCategory }) {
  const isMetric = category === 'Metric';
  return (
    <View style={[styles.badge, isMetric ? styles.badgeMetric : styles.badgeProcess]}>
      <Text style={[styles.badgeText, isMetric ? styles.badgeTextMetric : styles.badgeTextProcess]}>
        {category}
      </Text>
    </View>
  );
}

// ─── Follow-up result cell ────────────────────────────────────────────────────
// Renders its option list in a Modal, positioned from the button's measured screen
// coordinates. This lets the dropdown escape the table's ScrollView/card so it never
// gets clipped or needs scrolling to reach — it just floats over everything.
function ResultCell({
  item,
  onResultChange,
  isOpen,
  onToggle,
  isReadOnly,
}: {
  item: CoachingHistoryItem;
  onResultChange: (id: string, result: FollowUpResult) => void;
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  isReadOnly?: boolean;
}) {
  const result = item.followUpResult as FollowUpResult;
  const buttonRef = useRef<View>(null);
  const [menuLayout, setMenuLayout] = useState<{ top: number; left: number; width: number } | null>(null);

  const openMenu = () => {
    buttonRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setMenuLayout({ top: pageY + height + 4, left: pageX, width: Math.max(width, 200) });
    });
    onToggle(true);
  };
  const closeMenu = () => onToggle(false);

  const renderMenu = () => (
    <Modal visible={isOpen} transparent animationType="none" onRequestClose={closeMenu}>
      <Pressable style={StyleSheet.absoluteFill} onPress={closeMenu}>
        {menuLayout && (
          <View
            style={[
              styles.pickerOverlay,
              styles.pickerOverlayFloating,
              { top: menuLayout.top, left: menuLayout.left, width: menuLayout.width },
            ]}
          >
            {RESULT_OPTIONS.map((opt) => (
              <TouchableOpacity
                key={opt || 'pending'}
                style={styles.pickerOption}
                onPress={() => {
                  onResultChange(item.id, opt);
                  closeMenu();
                }}
              >
                <Text style={styles.pickerOptionText}>{resultOptionLabel(opt)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </Pressable>
    </Modal>
  );

  // Finalized history rows are locked; a still-open item can always be re-picked.
  if (item.status === 'Completed') {
    return (
      <View style={styles.completePill}>
        <Text style={styles.completePillText}>{item.followUpResult || 'Complete'}</Text>
      </View>
    );
  }

  if (result === 'Complete' || result === 'Shows Improvement' || result === 'No Improvement Shown') {
    const isComplete = result === 'Complete';
    return (
      <View style={styles.resultCellWrapper}>
        <TouchableOpacity
          ref={buttonRef}
          style={[styles.improvePill, isComplete && styles.completePillButton]}
          disabled={isReadOnly}
          onPress={() => (isOpen ? closeMenu() : openMenu())}
          accessibilityLabel={`Follow up result for ${item.focus}`}
        >
          <Text style={styles.improvePillText}>{result}</Text>
          <MaterialIcons name="keyboard-arrow-down" size={16} color="#FFFFFF" />
        </TouchableOpacity>
        {renderMenu()}
      </View>
    );
  }

  // Pending — amber outlined dropdown
  return (
    <View style={styles.resultCellWrapper}>
      <TouchableOpacity
        ref={buttonRef}
        style={styles.pendingDropdown}
        disabled={isReadOnly}
        onPress={() => (isOpen ? closeMenu() : openMenu())}
        accessibilityLabel={`Follow up result for ${item.focus}`}
      >
        <Text style={styles.pendingDropdownText}>
          {result || 'Pending, select a result'}
        </Text>
        <MaterialIcons name="keyboard-arrow-down" size={16} color="#D97706" />
      </TouchableOpacity>
      {renderMenu()}
    </View>
  );
}

// ─── Single coaching row (open or history) ────────────────────────────────────
function CoachingRow({
  item,
  onResultChange,
  onFeedbackChange,
  isReadOnly,
}: {
  item: CoachingHistoryItem;
  onResultChange: (id: string, result: FollowUpResult) => void;
  onFeedbackChange: (id: string, feedback: string) => void;
  isReadOnly?: boolean;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const showFeedback = requiresFeedback(item.followUpResult as FollowUpResult);
  const feedbackTooShort = showFeedback && item.followUpFeedback.trim().length < MIN_FEEDBACK_CHARS;

  return (
    // Single wrapper (instead of a Fragment) so its zIndex can rise above the rows
    // below it while the result picker is open — RN Web stacks siblings by DOM order.
    <View style={[styles.rowWrapper, dropdownOpen && styles.rowWrapperOpen]}>
      <View style={[styles.tableRow, dropdownOpen && styles.tableRowOpen]}>
        <View style={styles.colSource}>
          <Text style={styles.sourceText}>{item.source}</Text>
        </View>
        <View style={styles.colFocus}>
          <Text style={styles.focusText}>{item.focus}</Text>
        </View>
        <View style={styles.colType}>
          <CategoryBadge category={item.category} />
        </View>
        <View style={styles.colPlan}>
          <Text style={styles.planText}>{item.plan}</Text>
          {!!item.support && (
            <Text style={styles.supportText}>Support: {item.support}</Text>
          )}
        </View>
        <View style={styles.colDate}>
          <Text style={styles.dateText}>{formatDate(item.date)}</Text>
        </View>
        <View style={styles.colResult}>
          <ResultCell
            item={item}
            onResultChange={onResultChange}
            isOpen={dropdownOpen}
            onToggle={setDropdownOpen}
            isReadOnly={isReadOnly}
          />
        </View>
      </View>

      {showFeedback && (
        <View style={styles.followUpRow}>
          <Text style={styles.followUpLabel}>
            Supervisor Feedback for {item.focus}, minimum {MIN_FEEDBACK_CHARS} characters
          </Text>
          <TextInput
            style={[
              styles.followUpTextarea,
              feedbackTooShort && styles.followUpTextareaError,
            ]}
            value={item.followUpFeedback}
            onChangeText={(t) => onFeedbackChange(item.id, t)}
            editable={!isReadOnly}
            placeholder="Describe the improvement observed, the concern that remains, and any coaching discussed."
            placeholderTextColor="#B1B1B1"
            multiline
            textAlignVertical="top"
            accessibilityLabel={`Supervisor feedback for ${item.focus}`}
          />
          {feedbackTooShort && (
            <Text style={styles.followUpHint}>
              At least {MIN_FEEDBACK_CHARS} characters required.
            </Text>
          )}
        </View>
      )}
    </View>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const MAX_VISIBLE = 5;

const CoachingHistory: React.FC<CoachingHistoryProps> = ({
  openItems,
  historyItems,
  activityType,
  isLoading = false,
  isReadOnly = false,
  onFollowUpResultChange,
  onFollowUpFeedbackChange,
}) => {
  const [showAll, setShowAll] = useState(false);

  const activityLabel =
    activityType === 'pulse_check'
      ? 'Pulse Check'
      : activityType === 'full_observation'
      ? 'Full Observation'
      : 'Routine Observation';

  const hintText =
    activityType === 'pulse_check'
      ? 'Select Shows Improvement, No Improvement Shown, or Complete before closing the Pulse Check'
      : 'Select Shows Improvement, No Improvement Shown, or Complete on the open coaching item';

  // Merge: open items first, then sorted history
  const sortedHistory = [...historyItems].sort((a, b) => b.date.localeCompare(a.date));
  const allRows = [...openItems, ...sortedHistory];
  const visibleRows = showAll ? allRows : allRows.slice(0, MAX_VISIBLE);
  const hiddenCount = allRows.length - visibleRows.length;

  return (
    <SectionCard title="Coaching History" subtitle={hintText}>
      {/* Loading */}
      {isLoading && (
        <View style={styles.stateContainer}>
          <ActivityIndicator size="small" color={AdminColors.blue700} />
          <Text style={styles.stateText}>Loading coaching history…</Text>
        </View>
      )}

      {!isLoading && (
        <ScrollView
          horizontal={Platform.OS !== 'web'}
          showsHorizontalScrollIndicator={false}
          style={styles.tableContainer}
        >
          <View style={Platform.OS !== 'web' ? { minWidth: 700 } : undefined}>
            {/* Table header */}
            <View style={styles.tableHeaderRow}>
              <View style={styles.colSource}>
                <Text style={styles.thText}>Source</Text>
              </View>
              <View style={styles.colFocus}>
                <Text style={styles.thText}>Focus Area</Text>
              </View>
              <View style={styles.colType}>
                <Text style={styles.thText}>Action Type</Text>
              </View>
              <View style={styles.colPlan}>
                <Text style={styles.thText}>Action Plan</Text>
              </View>
              <View style={styles.colDate}>
                <Text style={styles.thText}>Activity Date</Text>
              </View>
              <View style={styles.colResult}>
                <Text style={styles.thText}>Follow Up Result</Text>
              </View>
            </View>

            {/* No open coaching item \u2014 shown above any history rows */}
            {openItems.length === 0 && (
              <View style={styles.noOpenItemBanner}>
                <View style={styles.noOpenItemIcon}>
                  <MaterialIcons name="check" size={18} color={AdminColors.green600} />
                </View>
                <View style={styles.noOpenItemTextWrap}>
                  <Text style={styles.noOpenItemTitle}>No coaching item is open for this employee.</Text>
                  <Text style={styles.noOpenItemSubtitle}>
                    One coaching item is open at a time. A new one may be created below, but it is not
                    required to close this {activityLabel}.
                  </Text>
                </View>
              </View>
            )}

            {/* Rows */}
            {visibleRows.map((item) => (
              <CoachingRow
                key={item.id}
                item={item}
                onResultChange={onFollowUpResultChange}
                onFeedbackChange={onFollowUpFeedbackChange}
                isReadOnly={isReadOnly}
              />
            ))}
          </View>
        </ScrollView>
      )}

      {/* Show more / less */}
      {!isLoading && hiddenCount > 0 && (
        <TouchableOpacity
          style={styles.showMoreRow}
          onPress={() => setShowAll(true)}
          accessibilityLabel={`Show ${hiddenCount} more coaching records`}
        >
          <Text style={styles.showMoreText}>
            Show {hiddenCount} more record{hiddenCount !== 1 ? 's' : ''}
          </Text>
        </TouchableOpacity>
      )}
      {!isLoading && showAll && allRows.length > MAX_VISIBLE && (
        <TouchableOpacity
          style={styles.showMoreRow}
          onPress={() => setShowAll(false)}
          accessibilityLabel="Show fewer coaching records"
        >
          <Text style={styles.showMoreText}>Show less</Text>
        </TouchableOpacity>
      )}
    </SectionCard>
  );
};

export default CoachingHistory;
